import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Activity,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Wrench,
  LoaderPinwheel,
  X,
  BarChart3,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Navbar from "../components/ui/Navbar";
import { hasPermission } from "../utils/permissions";

const API = process.env.REACT_APP_API_URL;

const MESES = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

function fmt(n) {
  return (n || 0).toLocaleString("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  });
}

export default function TCOPage() {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [dashboard, setDashboard] = useState({
    equipos: [],
    kpis: {},
    umbralAlerta: 70,
  });
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [filtroCliente, setFiltroCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [umbral, setUmbral] = useState("");

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroCliente) params.cliente = filtroCliente;
      const [dash, rank] = await Promise.all([
        axios.get(`${API}/tco/dashboard`, { headers, params }),
        axios.get(`${API}/tco/ranking`, {
          headers,
          params: { ...params, limite: 10 },
        }),
      ]);
      setDashboard(dash.data);
      setRanking(rank.data);
      setUmbral(dash.data.umbralAlerta?.toString() || "70");
    } catch (err) {
      toast.error("Error al cargar dashboard TCO");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [filtroCliente, token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    axios
      .get(`${API}/clientes`, { headers })
      .then(({ data }) => setClientes(data))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const handleVerDetalle = async (equipoId) => {
    setLoadingDetalle(true);
    try {
      const { data } = await axios.get(`${API}/tco/equipo/${equipoId}`, {
        headers,
      });
      setDetalle(data);
    } catch (err) {
      toast.error("Error al cargar detalle");
    } finally {
      setLoadingDetalle(false);
    }
  };

  const handleActualizarUmbral = async () => {
    if (!umbral || isNaN(Number(umbral))) return toast.error("Valor inválido");
    try {
      await axios.put(
        `${API}/tco/reglas`,
        { tcoAlertaPorcentaje: Number(umbral) },
        { headers },
      );
      toast.success("Umbral de alerta actualizado");
      fetchDashboard();
    } catch (err) {
      toast.error("Error al actualizar regla");
    }
  };

  const { equipos, kpis } = dashboard;
  const alertas = equipos.filter((e) => e.alerta);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Activity className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                TCO — Ciclo de Vida
              </h1>
              <p className="text-sm text-gray-500">
                Costo total de propiedad por equipo
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Todos los clientes</option>
              {clientes.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Alertas */}
        {alertas.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <span className="font-semibold text-orange-700">
                {alertas.length} equipo{alertas.length > 1 ? "s" : ""} con
                alerta de costo
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {alertas.map((e) => (
                <button
                  key={e.equipo._id}
                  onClick={() => handleVerDetalle(e.equipo._id)}
                  className="flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium hover:bg-orange-200 transition"
                >
                  <AlertTriangle className="h-3 w-3" />
                  {e.equipo.codigo} ({e.pctSobreCompra}% del costo de compra)
                </button>
              ))}
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            icon={<DollarSign />}
            label="Costo acumulado total"
            value={fmt(kpis.totalCostos)}
            color="purple"
          />
          <KpiCard
            icon={<BarChart3 />}
            label="Servicios registrados"
            value={kpis.totalServicios || 0}
            color="blue"
          />
          <KpiCard
            icon={<Wrench />}
            label="Equipos con costos"
            value={kpis.totalEquipos || 0}
            color="indigo"
          />
          <KpiCard
            icon={<AlertTriangle />}
            label="Alertas activas"
            value={alertas.length}
            color={alertas.length > 0 ? "orange" : "green"}
          />
        </div>

        {/* Configuración umbral */}
        {hasPermission("tco.configurar_reglas") && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-gray-700">
                Alerta cuando costo acumulado supere
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={umbral}
                  onChange={(e) => setUmbral(e.target.value)}
                  min="0"
                  max="500"
                  className="w-20 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-600">
                  % del costo de compra
                </span>
                <button
                  onClick={handleActualizarUmbral}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 transition"
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Ranking */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-600" /> Top equipos más
              costosos
            </h3>
            {loading ? (
              <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
                Cargando datos, por favor espera...
              </div>
            ) : ranking.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Sin datos de costos aún
              </div>
            ) : (
              <div className="space-y-2">
                {ranking.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition"
                    onClick={() => r._id && handleVerDetalle(r._id)}
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        i === 0
                          ? "bg-yellow-100 text-yellow-700"
                          : i === 1
                            ? "bg-gray-200 text-gray-600"
                            : i === 2
                              ? "bg-orange-100 text-orange-700"
                              : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {r.codigo || "Sin código"} · {r.serial || "—"}
                      </p>
                      <p className="text-xs text-gray-400">
                        {r.servicios} servicio{r.servicios !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-purple-700">
                        {fmt(r.costoAcumulado)}
                      </p>
                      {r.costoCompra > 0 && (
                        <p className="text-xs text-gray-400">
                          {Math.round((r.costoAcumulado / r.costoCompra) * 100)}
                          % compra
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detalle equipo seleccionado */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            {loadingDetalle ? (
              <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
                Cargando datos, por favor espera...
              </div>
            ) : !detalle ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-gray-400">
                <Activity className="h-10 w-10 mb-3 opacity-30" />
                <p className="text-sm">
                  Selecciona un equipo del ranking para ver su detalle
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-mono text-blue-700 font-bold">
                      {detalle.equipo.codigo}
                    </p>
                    <p className="text-xs text-gray-500">
                      {detalle.equipo.cliente?.name} ·{" "}
                      {detalle.equipo.sede?.name}
                    </p>
                  </div>
                  <button
                    onClick={() => setDetalle(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {detalle.alerta && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5 mb-3 flex items-center gap-2 text-xs text-orange-700">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    Costo acumulado ({detalle.pctSobreCompra}%) supera el umbral
                    del {detalle.umbralPct}%
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <MiniKpi
                    label="Costo compra"
                    value={fmt(detalle.equipo.costoCompra)}
                  />
                  <MiniKpi
                    label="Costo acumulado"
                    value={fmt(detalle.totales.costoTotal)}
                    highlight
                  />
                  <MiniKpi
                    label="Costo anual"
                    value={fmt(detalle.costoAnual)}
                  />
                  <MiniKpi
                    label="Servicios"
                    value={detalle.totales.servicios}
                  />
                </div>

                {/* Tendencia */}
                {detalle.tendencia?.length > 0 && (
                  <>
                    <p className="text-xs font-semibold text-gray-500 mb-2">
                      Tendencia mensual
                    </p>
                    <ResponsiveContainer width="100%" height={120}>
                      <AreaChart
                        data={detalle.tendencia.map((d) => ({
                          name: `${MESES[d._id.month - 1]}`,
                          total: d.total,
                        }))}
                      >
                        <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                        <YAxis
                          tick={{ fontSize: 9 }}
                          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                        />
                        <Tooltip formatter={(v) => fmt(v)} />
                        <Area
                          type="monotone"
                          dataKey="total"
                          stroke="#7C3AED"
                          fill="#EDE9FE"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </>
                )}

                {/* Desglose */}
                <div className="mt-3 space-y-1">
                  {[
                    { label: "Mano de obra", val: detalle.totales.manoObra },
                    { label: "Repuestos", val: detalle.totales.repuestos },
                    { label: "Viáticos", val: detalle.totales.viaticos },
                    {
                      label: "Desplazamientos",
                      val: detalle.totales.desplazamientos,
                    },
                  ]
                    .filter((d) => d.val > 0)
                    .map((d, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-gray-500">{d.label}</span>
                        <span className="font-medium text-gray-700">
                          {fmt(d.val)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lista todos los equipos */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">
              Todos los equipos con costos ({equipos.length})
            </h3>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
              <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
              Cargando datos, por favor espera...
            </div>
          ) : equipos.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              <Activity className="h-10 w-10 mx-auto mb-2 opacity-30" />
              Sin datos TCO. Sincroniza gestiones en el módulo de Costos.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Equipo
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Cliente
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">
                      Costo compra
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">
                      Costo acumulado
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">
                      % compra
                    </th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">
                      Servicios
                    </th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">
                      Alerta
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {equipos.map((e) => (
                    <tr
                      key={e.equipo._id}
                      onClick={() => handleVerDetalle(e.equipo._id)}
                      className={`hover:bg-purple-50 cursor-pointer transition ${e.alerta ? "bg-orange-50/50" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-mono text-blue-700 font-medium text-xs">
                          {e.equipo.codigo}
                        </p>
                        <p className="text-xs text-gray-400">
                          {e.equipo.serial}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {e.equipo.cliente || "—"}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 text-xs">
                        {e.equipo.costoCompra > 0
                          ? fmt(e.equipo.costoCompra)
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-purple-700 text-xs">
                        {fmt(e.costoAcumulado)}
                      </td>
                      <td className="px-4 py-3 text-right text-xs">
                        {e.pctSobreCompra !== null ? (
                          <span
                            className={
                              e.alerta
                                ? "text-orange-600 font-bold"
                                : "text-gray-600"
                            }
                          >
                            {e.pctSobreCompra}%
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 text-xs">
                        {e.serviciosTotales}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {e.alerta ? (
                          <AlertTriangle className="h-4 w-4 text-orange-500 mx-auto" />
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, color }) {
  const colors = {
    purple: "bg-purple-50 text-purple-700 border-purple-100",
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
    orange: "bg-orange-50 text-orange-700 border-orange-100",
    green: "bg-green-50 text-green-700 border-green-100",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2 opacity-70">
        {React.cloneElement(icon, { className: "h-4 w-4" })}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}

function MiniKpi({ label, value, highlight }) {
  return (
    <div
      className={`rounded-lg p-2.5 border ${highlight ? "bg-purple-50 border-purple-200" : "bg-gray-50 border-gray-100"}`}
    >
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={`text-sm font-bold mt-0.5 ${highlight ? "text-purple-700" : "text-gray-800"}`}
      >
        {value}
      </p>
    </div>
  );
}
